"use client";

import React from "react";
import { Phone, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, HardHat, Building2, House as Home, Hammer } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const accessTypes = [{
  icon: Building2,
  title: "Commercial",
  description: "Main contractors & developers"
}, {
  icon: Home,
  title: "Residential",
  description: "Extensions, renovations & domestic"
}, {
  icon: Hammer,
  title: "Roofing",
  description: "Chimneys, re-roofs & solar access"
}, {
  icon: HardHat,
  title: "Specialist",
  description: "Temporary roofs & edge protection"
}];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function HeroBasingstoke() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="heroscaffoldingbasingstoke" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground pt-32 pb-20 md:pt-40 md:pb-28 lg:pt-44 lg:pb-32">
      {/* Subtle architectural background grid & radial accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/15 via-secondary to-secondary opacity-70" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] [background-size:4rem_4rem]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-3xl">
          {/* Trust eyebrow */}
          <motion.div variants={itemVariants} className="mb-6 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-sidebar-border bg-sidebar-accent/50 text-secondary-foreground font-normal px-3 py-1 text-xs sm:text-sm tracking-wide">
              <ShieldCheck className="size-3.5 text-primary-foreground" />
              <span>Safety-Led Scaffolding Contractor</span>
            </Badge>
            <span className="hidden sm:inline text-xs text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">Basingstoke & Hampshire</span>
          </motion.div>

          {/* Main H1 */}
          <motion.h1 variants={itemVariants} className="font-default text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground">
            Scaffolding in Basingstoke
          </motion.h1>

          {/* Proposition text */}
          <motion.p variants={itemVariants} className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed font-default">
            Safe, reliable access for commercial developments, residential projects, roofing contractors, and specialist sites across Basingstoke and the surrounding Hampshire region.
          </motion.p>

          {/* Call to action buttons */}
          <motion.div variants={itemVariants} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold text-base h-12 px-7 transition-[background-color,box-shadow]">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                <span>Request a quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar text-secondary-foreground hover:bg-sidebar-accent hover:text-secondary-foreground font-semibold text-base h-12 px-6 shadow-sm transition-[background-color,border-color,color]">
              <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground hover:text-secondary-foreground">
                <Phone className="size-4 text-primary-foreground" />
                <span>Call 01344 929900</span>
              </a>
            </Button>
          </motion.div>

          {/* Accreditations checkstrip */}
          <motion.div variants={itemVariants} className="mt-10 pt-6 border-t border-sidebar-border flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-muted-foreground">
            <span className="font-medium text-secondary-foreground">Verified Standards:</span>
            {accreditations.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary-foreground shrink-0" />
                <span>{item}</span>
              </div>)}
          </motion.div>
        </motion.div>

        {/* Access specialisms grid */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {accessTypes.map((access, i) => {
          const IconComponent = access.icon;
          return <div key={access.title} data-index={i} className="group rounded-lg border border-sidebar-border bg-sidebar/70 backdrop-blur-xs p-5 transition-[border-color,background-color] hover:border-primary/50 hover:bg-sidebar">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-md bg-sidebar-accent text-secondary-foreground">
                    <IconComponent className="size-5" />
                  </div>
                  <div>
                    <h2 className="font-default text-base font-semibold text-secondary-foreground">
                      {access.title}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {access.description}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </motion.div>
      </div>
    </section>;
}